import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Button, Typography } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { PageLayout } from '@/shared/components/PageLayout';
import { Drawer } from '@/shared/components/Drawer';
import { FormEventChainLink } from './components/FormEventChainLink';
import { EventChainLinksTable, rowsPerPageSetting } from './components/EventChainLinksTable';
import { useGetEventChainLinks } from './services/hooks/use-get-event-chain-links';
import { useUpdateEventChainLink } from './services/hooks/use-update-event-chain-link';
import { FILTER_PARAMS, parseFilters, serializeFilters } from './services';
const DEFAULT_PAGE_SIZE = 50;
const PAGE_NUMBER_PARAM = 'pageNumber';
const PAGE_SIZE_PARAM = 'pageSize';
// Пагинация хранится в query-параметрах, чтобы после F5 открывалась та же страница.
// Невалидные значения из адресной строки заменяются значениями по умолчанию.
const parsePagination = (search) => {
    const params = new URLSearchParams(search);
    const pageNumber = Number(params.get(PAGE_NUMBER_PARAM));
    const pageSize = Number(params.get(PAGE_SIZE_PARAM));
    return {
        pageNumber: Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : 1,
        pageSize: rowsPerPageSetting.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
    };
};
export const EventChainLinks = () => {
    const history = useHistory();
    const { search } = useLocation();
    const { pageNumber, pageSize } = parsePagination(search);
    const filters = parseFilters(new URLSearchParams(search));
    const [isOpenAddDrawer, setIsOpenAddDrawer] = useState(false);
    const [isOpenLinkDrawer, setIsOpenLinkDrawer] = useState(false);
    const [targetLink, setTargetLink] = useState();
    const { data: linksList } = useGetEventChainLinks({ pageSize, pageNumber, ...filters });
    const { mutate: updateLink, isLoading: isTogglingEnabled, variables: toggleVariables } = useUpdateEventChainLink();
    const setPagination = (nextPageNumber, nextPageSize, replace = false) => {
        const params = new URLSearchParams(search);
        params.set(PAGE_NUMBER_PARAM, String(nextPageNumber));
        params.set(PAGE_SIZE_PARAM, String(nextPageSize));
        const location = { search: `?${params.toString()}` };
        if (replace) {
            history.replace(location);
        }
        else {
            history.push(location);
        }
    };
    const totalPages = linksList?.totalPages ?? 0;
    // После удаления строк или ручной правки URL страница может оказаться за пределами списка.
    useEffect(() => {
        if (totalPages > 0 && pageNumber > totalPages) {
            setPagination(totalPages, pageSize, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalPages, pageNumber, pageSize]);
    const handleClickRow = (data) => {
        setTargetLink(data);
        setIsOpenLinkDrawer(true);
    };
    // Отправляем запись целиком, как при сохранении формы; меняется только enabled.
    const handleToggleEnabled = (row, enabled) => {
        updateLink({
            guid: row.guid,
            eventCode: row.eventCode,
            eventLinkDescription: row.eventLinkDescription,
            chainTypeGuid: row.chainTypeGuid,
            enabled,
        });
    };
    // Берём history.location, а не search из замыкания: фильтр "содержит" применяется с задержкой,
    // и за это время другие фильтры уже могли поменяться.
    // replace - чтобы набор текста в фильтре не засорял историю браузера.
    const handleChangeFilters = (patch) => {
        const params = new URLSearchParams(history.location.search);
        const nextFilters = { ...parseFilters(params), ...patch };
        Object.values(FILTER_PARAMS).forEach((param) => params.delete(param));
        Object.entries(serializeFilters(nextFilters)).forEach(([param, value]) => params.set(param, value));
        // Со сменой фильтра текущий номер страницы теряет смысл.
        params.set(PAGE_NUMBER_PARAM, '1');
        params.set(PAGE_SIZE_PARAM, String(pageSize));
        history.replace({ search: `?${params.toString()}` });
    };
    const handleChangePage = (page) => {
        setPagination(page, pageSize);
    };
    const handleChangeRowsPerPage = (size) => {
        setPagination(1, size);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(PageLayout, null,
            React.createElement(Box, { height: "75vh" },
                React.createElement(Box, { mb: 1 },
                    React.createElement(Typography, { variant: "h4" }, "\u0421\u043E\u0431\u044B\u0442\u0438\u044F \u0441\u0432\u044F\u0437\u0438")),
                React.createElement(Box, { mb: 2 },
                    React.createElement(Button, { variant: "contained", color: "primary", startIcon: React.createElement(AddCircleIcon, null), onClick: () => setIsOpenAddDrawer(true) }, "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C")),
                React.createElement(EventChainLinksTable, { rows: linksList?.links ?? [], onClickRow: handleClickRow, filterOptions: linksList?.filterOptions, filters: filters, onChangeFilters: handleChangeFilters, onToggleEnabled: handleToggleEnabled, pendingToggle: isTogglingEnabled ? toggleVariables : undefined, totalCount: linksList?.totalCount ?? 0, totalPages: totalPages, pageSize: pageSize, currentPage: pageNumber, onChangePage: handleChangePage, onChangeRowsPerPage: handleChangeRowsPerPage }))),
        React.createElement(Drawer, { isOpen: isOpenAddDrawer, onClose: () => setIsOpenAddDrawer(false) }, isOpenAddDrawer && React.createElement(FormEventChainLink, { onSubmit: () => setIsOpenAddDrawer(false) })),
        React.createElement(Drawer, { isOpen: isOpenLinkDrawer, onClose: () => setIsOpenLinkDrawer(false) },
            React.createElement(FormEventChainLink, { key: targetLink?.guid, isEditing: true, initialState: targetLink, onSubmit: () => setIsOpenLinkDrawer(false) }))));
};
